import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (n) => (n < 0 ? "-$" : "$") + Math.abs(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 });
const pct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}%`);
const cls = (n) => (n > 0 ? "text-seared" : n < 0 ? "text-seagreen" : "text-mutedsea");

export default function PremiumChange() {
  const nav = useNavigate();
  const [f, setF] = useState({ group_by: "policy", producer: "all", carrier: "all", lob: "" });
  const [producers, setProducers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/lookups/owners").then(({ data }) => setProducers(data.items || [])).catch(() => {});
    api.get("/lookups/carriers").then(({ data }) => setCarriers(data.items || [])).catch(() => {});
  }, []);

  const load = () => {
    const p = new URLSearchParams({ group_by: f.group_by });
    if (f.producer !== "all") p.set("producer", f.producer);
    if (f.carrier !== "all") p.set("carrier", f.carrier);
    if (f.lob) p.set("lob", f.lob);
    api.get(`/agency/reports/premium-change?${p.toString()}`).then(({ data }) => setData(data)).catch(() => toast.error("Load failed"));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [JSON.stringify(f)]);

  return (
    <div className="p-6" data-testid="premium-change-page">
      <PageHeader title="Premium Change" subtitle="Current vs prior effective-dated term premium. Historical terms are never overwritten." />

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4" data-testid="pc-summary">
          <div className="bg-white border border-border rounded-sm p-4"><div className="text-xs uppercase text-mutedsea">Total Premium Change</div><div className={`font-serif text-2xl mt-1 ${cls(data.summary.total_change)}`}>{money(data.summary.total_change)}</div></div>
          <div className="bg-white border border-border rounded-sm p-4"><div className="text-xs uppercase text-mutedsea">Avg % Change</div><div className={`font-serif text-2xl mt-1 ${cls(data.summary.average_percent_change)}`}>{pct(data.summary.average_percent_change)}</div></div>
          <div className="bg-white border border-border rounded-sm p-4"><div className="text-xs uppercase text-mutedsea">Policies Compared</div><div className="font-serif text-2xl text-navy mt-1">{data.summary.policies_compared}</div></div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Select value={f.group_by} onValueChange={(v) => setF({ ...f, group_by: v })}><SelectTrigger className="w-40 h-9" data-testid="pc-groupby"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{[["policy", "By Policy"], ["client", "By Client"], ["carrier", "By Carrier"], ["producer", "By Producer"], ["lob", "By Line of Business"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
        <Select value={f.producer} onValueChange={(v) => setF({ ...f, producer: v })}><SelectTrigger className="w-40 h-9" data-testid="pc-producer"><SelectValue placeholder="Producer" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="all">All Producers</SelectItem>{producers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
        <Select value={f.carrier} onValueChange={(v) => setF({ ...f, carrier: v })}><SelectTrigger className="w-40 h-9" data-testid="pc-carrier"><SelectValue placeholder="Carrier" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="all">All Carriers</SelectItem>{carriers.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Input value={f.lob} onChange={(e) => setF({ ...f, lob: e.target.value })} placeholder="Line of Business" className="w-44 h-9" data-testid="pc-lob" />
      </div>

      {data && f.group_by !== "policy" && data.grouped.length > 0 && (
        <div className="bg-white border border-border rounded-sm overflow-x-auto mb-4" data-testid="pc-grouped">
          <table className="w-full text-sm"><thead><tr className="bg-cloud border-b border-border">{["Group", "Policies", "Prior", "Current", "$ Change", "% Change"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold">{h}</th>)}</tr></thead>
            <tbody>{data.grouped.map((g) => <tr key={g.key} className="border-b border-border last:border-0"><td className="px-3 py-2 text-slate">{g.key}</td><td className="px-3 py-2">{g.count}</td><td className="px-3 py-2">{money(g.prior)}</td><td className="px-3 py-2">{money(g.current)}</td><td className={`px-3 py-2 ${cls(g.dollar_change)}`}>{money(g.dollar_change)}</td><td className={`px-3 py-2 ${cls(g.percent_change)}`}>{pct(g.percent_change)}</td></tr>)}</tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-border rounded-sm overflow-x-auto end-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-cloud border-b border-border">{["Client", "Policy #", "Carrier", "LOB", "Producer", "Prior", "Current", "$ Change", "% Change", "Effective"].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-navy font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody data-testid="pc-table">
            {(!data || data.items.length === 0) && <tr><td colSpan={10} className="px-3 py-6 text-center text-mutedsea">No policies with two or more terms to compare.</td></tr>}
            {data && data.items.map((r) => (
              <tr key={r.policy_id} data-testid={`pc-row-${r.policy_id}`} onClick={() => nav(`/policies/${r.policy_id}`)} className="border-b border-border last:border-0 hover:bg-ice cursor-pointer">
                <td className="px-3 py-2 text-slate">{r.client}</td>
                <td className="px-3 py-2 text-atlantic">{r.policy_number}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.carrier}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.line}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.producer}</td>
                <td className="px-3 py-2">{money(r.prior_premium)}</td>
                <td className="px-3 py-2">{money(r.current_premium)}</td>
                <td className={`px-3 py-2 ${cls(r.dollar_change)}`}>{money(r.dollar_change)}</td>
                <td className={`px-3 py-2 ${cls(r.percent_change)}`}>{pct(r.percent_change)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-mutedsea">{r.current_effective}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
